import { ValidationError } from '../../../shared/exceptions/validation.error';
import type { BusinessUnderwritingMessagesResponse } from '../../quotes/types';
import { BaseService, notifyInactiveBroker } from '../../base';
import type {
  AttachPaymentInfoResponse,
  AttachQuotePayload,
  CalculateProposalResponse,
  CreateProposalAnalysisRecordPayload,
  DecideProposalAnalysisPayload,
  GeneratePdfPayload,
  GeneratePdfResponse,
  GetProposalDocumentResponse,
  PaymentInfoPayload,
  Proposal,
  ProposalAnalysisRecord,
  TransmitProposalResponse,
  TransmitProposalResult,
} from '../types';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;
const PRODUCT_PREFIX = '/portal/v1/business/proposals';

/**
 * Serviço específico para proposta empresarial. Espelha a
 * `HomeownerProposalsService`, mas aponta para as rotas do
 * `BusinessProposalsController`. A escolha entre as duas é feita pela
 * fachada `proposalsService` em função do `productCode` recebido.
 */
export class BusinessProposalsService extends BaseService {
  constructor() {
    super();
  }

  async attachPaymentInfo(
    policyId: string,
    payload: PaymentInfoPayload
  ): Promise<AttachPaymentInfoResponse> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/attach-element`;
      const response = await this.post(path, payload, PORTAL_API_URL);

      if (response.status === 201) {
        const json = await this.handleJsonResponse<unknown>(response);
        return {
          success: true,
          message: json.message,
        };
      }

      const json = await this.handleJsonResponse<unknown>(response);
      return {
        success: false,
        error: json.error || 'Erro ao anexar informações de pagamento',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao anexar informações de pagamento',
      };
    }
  }

  async updateProposal(
    proposalId: string,
    payload: Partial<AttachQuotePayload>
  ): Promise<{
    success: boolean;
    data?: Proposal;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/attach-element`;
      const response = await this.post(path, payload, PORTAL_API_URL);

      if (response.status === 422) {
        throw buildAttachElementValidationError(
          (await response.json().catch(() => null)) as {
            detail?: string;
            errors?: Record<string, { code?: string; message?: string }>;
          } | null
        );
      }

      return this.handleJsonResponse<Proposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;

      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao carregar proposta',
      };
    }
  }

  async calculateProposal(
    policyId: string
  ): Promise<CalculateProposalResponse> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/calculate`;
      const response = await this.post(path, {}, PORTAL_API_URL);

      if (!response.ok) {
        const json = await this.handleJsonResponse<unknown>(response);
        return {
          success: false,
          error:
            json.error ||
            `Erro ao calcular proposta (${response.status}: ${response.statusText})`,
        };
      }

      const json = await this.handleJsonResponse<unknown>(response);

      if (!json.success) {
        return {
          success: false,
          error: json.error || 'Erro ao calcular proposta',
        };
      }

      return {
        success: true,
        data: json.data,
        message: json.message,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro inesperado ao calcular proposta',
      };
    }
  }

  async generatePdf(
    proposalId: string,
    payload: GeneratePdfPayload
  ): Promise<{
    success: boolean;
    data?: GeneratePdfResponse;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/pdf`;
      const response = await this.post(path, payload, {}, PORTAL_API_URL);

      if (!response.ok) {
        return await extractPdfError(response);
      }

      const data = await this.handleJsonResponse<GeneratePdfResponse>(response);

      if (!data.success || !data.data) {
        return {
          success: false,
          error: data.error || 'Erro ao gerar URL do PDF',
        };
      }

      return { success: true, data: data.data };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao gerar PDF da proposta',
      };
    }
  }

  async getProposalDocument(proposalId: string): Promise<{
    success: boolean;
    data?: GetProposalDocumentResponse;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/documents`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<GetProposalDocumentResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar documento da proposta',
      };
    }
  }

  async transmitProposal(
    policyId: string
  ): Promise<TransmitProposalResponse> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/transmit`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      const result =
        await this.handleJsonResponse<TransmitProposalResult>(response);

      if (!result.success) {
        return {
          success: false,
          error: result.error,
        };
      }

      const payload = result.data;
      return {
        success: true,
        sentToAnalysis: payload?.sentToAnalysis === true,
        message: payload?.message ?? result.message,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao transmitir proposta',
      };
    }
  }

  async getAnalysisRecords(policyId: string): Promise<{
    success: boolean;
    data?: ProposalAnalysisRecord[];
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(policyId)}/analysis/records`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<ProposalAnalysisRecord[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar interações da análise',
      };
    }
  }

  async createAnalysisRecord(
    policyId: string,
    payload: CreateProposalAnalysisRecordPayload
  ): Promise<{
    success: boolean;
    data?: ProposalAnalysisRecord;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(policyId)}/analysis/records`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      return this.handleJsonResponse<ProposalAnalysisRecord>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao registrar interação na análise',
      };
    }
  }

  async decideAnalysis(
    policyId: string,
    payload: DecideProposalAnalysisPayload
  ): Promise<{
    success: boolean;
    data?: unknown;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(policyId)}/analysis/decision`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      return this.handleJsonResponse<unknown>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao registrar decisão da análise',
      };
    }
  }

  async getUnderwritingMessages(policyId: string): Promise<{
    success: boolean;
    data?: BusinessUnderwritingMessagesResponse;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(policyId)}/underwriting-messages`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<BusinessUnderwritingMessagesResponse>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar mensagens de subscrição da proposta',
      };
    }
  }
}

/**
 * Converte o ProblemDetails de um 422 do attach-element em `ValidationError`,
 * preservando os `fieldErrors` estruturados (ex.: `proposalStatus.code === '2'`
 * quando a cotação foi encerrada) para o componente decidir o tratamento.
 */
function buildAttachElementValidationError(
  data: {
    detail?: string;
    errors?: Record<string, { code?: string; message?: string }>;
  } | null
): ValidationError {
  const fieldErrors = Object.fromEntries(
    Object.entries(data?.errors ?? {}).map(([key, value]) => [
      key,
      {
        code: String(value?.code ?? '').trim(),
        message: String(value?.message ?? '').trim(),
      },
    ])
  );

  // `proposalStatus` é um eco do status da proposta, não um erro de campo —
  // fica fora das mensagens exibidas como lista.
  const messages = Object.entries(fieldErrors)
    .filter(([key]) => key !== 'proposalStatus')
    .map(([, error]) => error.message)
    .filter(Boolean);

  const validationError = new ValidationError(messages);
  validationError.detail = String(data?.detail ?? '').trim() || undefined;
  validationError.fieldErrors = fieldErrors;
  return validationError;
}

async function extractPdfError(
  response: Response
): Promise<{ success: false; error: string }> {
  const text = (await response.text()).trim();
  let apiMessage: string | undefined;

  if (text) {
    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      const problemDetail = typeof data.detail === 'string' && data.detail;
      const direct =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error);
      const problemTitle = typeof data.title === 'string' && data.title;
      const errors = data.errors;

      if (problemDetail) apiMessage = problemDetail;
      else if (direct) apiMessage = direct;
      else if (problemTitle) apiMessage = problemTitle;
      else if (Array.isArray(errors) && errors.length > 0) {
        const first = errors[0] as unknown;
        if (typeof first === 'string') apiMessage = first;
        else if (first && typeof first === 'object') {
          const e = first as Record<string, unknown>;
          const msg = typeof e.message === 'string' && e.message;
          if (msg) apiMessage = msg;
        }
      }
    } catch {
      apiMessage = text;
    }
  }

  // Este caminho não passa pelo `handleJsonResponse` (ex.: PDF): avisa aqui.
  notifyInactiveBroker(response.status, apiMessage);

  return {
    success: false,
    error: apiMessage || `Erro ${response.status}: ${response.statusText}`,
  };
}

export const businessProposalsService = new BusinessProposalsService();
